/**
 * Custom-field SKU column helpers
 *
 * Shared logic for rendering admin-configured custom-field columns in the
 * Bill of Materials (MaterialsList) and Product Placement Guide tables. Keeps
 * the "replace / alongside / standalone the Product # column" behavior in one
 * place so both tables (and the PDF) stay consistent.
 *
 * Live-only: there is no snapshot. The column config + values come from the
 * product table loaded for the current retailer context (getCustomFieldConfig
 * / getCustomFieldsForProduct in src/data/productTable), so edits to a
 * retailer's fields reflect immediately on saved/shared estimates too.
 */

import React from 'react';
import { CustomFieldColumnConfig, ProductCustomField } from '../types';

export interface SkuColumn {
  /** field_key for custom columns, or a sentinel for the built-in column. */
  key: string;
  heading: string;
  /** True for the built-in Trusscore "Product #" column. */
  isBuiltIn: boolean;
  /** Render the resolved URL as a link (url-type custom fields). */
  linkable: boolean;
  /** Replace-mode columns fall back to the product number when value is empty. */
  fallbackToProductNumber: boolean;
}

const BUILT_IN_KEY = '__product_number__';
const DEFAULT_HEADING = 'PRODUCT #';

/**
 * Build the ordered list of SKU columns for a table scope, given the retailer's
 * custom-field column config. With no applicable config this returns just the
 * built-in Product # column — identical to legacy output.
 *
 * Layout: [alongside fields] · [built-in OR replace field] · [standalone fields]
 * (each group ordered by `order`). A field's productNumberHeadingOverride
 * relabels the built-in column (e.g. "Trusscore SKU").
 */
export function buildSkuColumns(
  config: CustomFieldColumnConfig[] | undefined,
  scope: 'bom' | 'placement',
): SkuColumn[] {
  const applicable = (config ?? [])
    .filter((c) => (scope === 'bom' ? c.showInBOM : c.showInPlacement))
    .slice()
    .sort((a, b) => a.order - b.order);

  const builtInHeadingOverride = applicable.find((c) => c.productNumberHeadingOverride)?.productNumberHeadingOverride;
  const builtIn: SkuColumn = {
    key: BUILT_IN_KEY,
    heading: builtInHeadingOverride || DEFAULT_HEADING,
    isBuiltIn: true,
    linkable: false,
    fallbackToProductNumber: false,
  };

  const toCol = (c: CustomFieldColumnConfig, fallback: boolean): SkuColumn => ({
    key: c.fieldKey,
    heading: c.heading,
    isBuiltIn: false,
    linkable: c.linkable,
    fallbackToProductNumber: fallback,
  });

  const before: SkuColumn[] = [];
  const after: SkuColumn[] = [];
  let replacement: SkuColumn | null = null;

  for (const c of applicable) {
    if (c.mode === 'replace') {
      // First replace wins; later replaces degrade to standalone.
      if (!replacement) replacement = toCol(c, true);
      else after.push(toCol(c, false));
    } else if (c.mode === 'alongside') {
      before.push(toCol(c, false));
    } else {
      after.push(toCol(c, false));
    }
  }

  return [...before, replacement ?? builtIn, ...after];
}

/**
 * Render the contents of a SKU cell for a given column + material item.
 * `getFields` resolves a product's custom-field values (live lookup).
 */
export function renderSkuCell(
  col: SkuColumn,
  item: { productNumber: string; productID: string },
  getFields: (productID: string) => ProductCustomField[],
): React.ReactNode {
  if (col.isBuiltIn) return item.productNumber;

  const field = getFields(item.productID).find((f) => f.fieldKey === col.key);
  const value = field?.value;

  if (value === null || value === undefined || value === '') {
    // A replace column with no value must still identify the row.
    return col.fallbackToProductNumber ? item.productNumber : '—';
  }

  if (col.linkable && field?.resolvedUrl) {
    return (
      <a href={field.resolvedUrl} target="_blank" rel="noopener noreferrer">
        {value}
      </a>
    );
  }
  return value;
}

/**
 * Plain-text version of a SKU cell for non-React surfaces (PDF). Returns the
 * value string (or product number / em-dash fallback) without any link markup.
 */
export function skuCellText(
  col: SkuColumn,
  item: { productNumber: string; productID: string },
  getFields: (productID: string) => ProductCustomField[],
): string {
  if (col.isBuiltIn) return item.productNumber;
  const field = getFields(item.productID).find((f) => f.fieldKey === col.key);
  const value = field?.value;
  if (value === null || value === undefined || value === '') {
    return col.fallbackToProductNumber ? item.productNumber : '—';
  }
  return value;
}
